import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useUser } from '../contexts/userContext';
import { API, graphqlOperation } from 'aws-amplify';
import {
  // createUserNotification,
  markNotificationAsRead,
  markNotificationAsUnread,
} from '../generated/graphql/mutations';
// import { v4 as uuidv4 } from 'uuid';
import { listNotifications, getNotificationCount, listUnreadNotifications } from '../generated/graphql/queries';

import * as FaIcons from 'react-icons/fa';
import * as FiIcons from 'react-icons/fi';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';

const NotificationContext = createContext({
  notifications: [],
  notificationCount: 0,
});

const NotificationProvider = ({ children }) => {
  const { user } = useUser();
  const [notifications, setNotifications] = useState([]);
  const [unreadNotifications, setUnreadNotifications] = useState([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const [nextToken, setNextToken] = useState(null);
  const [lastEvaluated, setLastEvaluated] = useState(null);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);

  const nextTokenRef = React.useRef(nextToken);
  useEffect(() => {
    nextTokenRef.current = nextToken;
  }, [nextToken]);

  // const addNotification = useCallback(
  //   async notification => {
  //     setLoading(true);
  //     const { recipients: recipientParams, ...params } = notification;
  //     const recipients = recipientParams.split(',');
  //     // console.log({ recipients, params });

  //     const resultIds = [];
  //     if (Array.isArray(recipients) && recipients.length > 0) {
  //       // recipients.forEach(async recipient => {
  //       for (const recipient of recipients) {
  //         const data = {
  //           ...params,
  //           recipient,
  //         };
  //         const id = await processNotification(data);
  //         resultIds.push(id);
  //       }
  //       // });
  //     } else {
  //       const id = await processNotification({
  //         recipient: recipientParams,
  //         params,
  //       });
  //       resultIds.push(id);
  //     }

  //     setLoading(false);
  //     return resultIds;
  //   },
  //   [setNotifications]
  // );

  const queryNotificationCount = useCallback(async () => {
    try {
      const {
        data: { getNotificationCount: data = null },
      } = await API.graphql(graphqlOperation(getNotificationCount));
      // console.log('getNotificationCount data', data);
      if (!data) {
        setNotificationCount(0);
        return;
      }
      const { count } = data;
      setNotificationCount(count || 0);
    } catch (error) {
      console.error('Error fetching notification count: ', error);
    }
  }, []);

  useEffect(() => {
    queryNotificationCount();
  }, [queryNotificationCount]);

  const markAsRead = useCallback(
    async notificationId => {
      setUpdating(true);
      try {
        const updatedNotification = await API.graphql(
          graphqlOperation(markNotificationAsRead, {
            id: notificationId,
          })
        );
        // console.log('Updated notification:', updatedNotification);
        // loop notifications and this item with the updated one
        setNotifications(prev =>
          prev.map(n =>
            // n.id === notificationId ? { ...n, readAt: updatedNotification.data.markNotificationAsRead.readAt } : n
            n.id === notificationId ? { ...n, ...updatedNotification.data.markNotificationAsRead } : n
          )
        );

        // Remove notification from unread notifications list
        setUnreadNotifications(prev => prev.filter(n => n.id !== notificationId));

        // Increment the notification count
        setNotificationCount(prevCount => {
          if (prevCount <= 0) {
            return 0; // Ensure count doesn't go negative
          }
          return prevCount - 1;
        });
        return updatedNotification.data.markNotificationAsRead;
        // setNotifications(prev =>
        //   prev.map(n =>
        //     n.id === notificationId ? { ...n, readAt: updatedNotification.data.createUserNotification.readAt } : n
        //   )
        // );
      } catch (error) {
        console.error('Error marking notification as read:', error);
      } finally {
        setUpdating(false);
      }
    },
    [setNotifications, setNotificationCount]
  );

  // useEffect(() => {
  //   console.log('Notification count updated:', notificationCount);
  // }, [notificationCount]);

  // useEffect(() => {
  //   console.log('Next token updated:', nextToken);
  // }, [nextToken]);

  const markAsUnread = useCallback(
    async notificationId => {
      setUpdating(true);
      try {
        const updatedNotification = await API.graphql(
          graphqlOperation(markNotificationAsUnread, {
            id: notificationId,
          })
        );
        // setNotificationCount(55);
        // console.log('Updated notification:', updatedNotification);
        // loop notifications and this item with the updated one
        setNotifications(prev =>
          prev.map(n =>
            // n.id === notificationId ? { ...n, readAt: updatedNotification.data.markNotificationAsRead.readAt } : n
            n.id === notificationId ? { ...n, ...updatedNotification.data.markNotificationAsUnread } : n
          )
        );

        // Add notification to unread notifications list
        setUnreadNotifications(prev => [...prev, updatedNotification.data.markNotificationAsUnread]);


        // Increment the notification count
        setNotificationCount(prevCount => prevCount + 1);

        return updatedNotification.data.markNotificationAsUnread;
        // setNotifications(prev =>
        //   prev.map(n =>
        //     n.id === notificationId ? { ...n, readAt: updatedNotification.data.createUserNotification.readAt } : n
        //   )
        // );
      } catch (error) {
        console.error('Error marking notification as unread:', error);
      } finally {
        setUpdating(false);
      }
    },
    [setNotifications, setNotificationCount]
  );

  const notificationCountMemo = useMemo(() => {
    return notificationCount > 0 ? notificationCount : 0;
  }, [notificationCount]);

  const resetNotifications = useCallback(() => {
    setNextToken(null);
    setLastEvaluated(null);
    setNotifications([]);
  }, []);

  const getIcon = notification => {
    switch (notification.type) {
      case 'CASE_SUBMITTED':
        return <FaIcons.FaBriefcaseMedical size="40" />;

      case 'CASE_SUBMITTED_AFTER_RETURN':
      case 'CASE_RESUBMITTED':
        // return <FaIcons.FaBriefcaseMedical size="40" />;
        return (
          <FontAwesomeIcon
            fontSize={40}
            mask={icon({ name: 'briefcase-blank', style: 'solid' })}
            icon={icon({ name: 'rotate', style: 'solid' })}
            transform="shrink-9 down-1 flip-h"
            // mask="fa-solid fa-circle"
          />
        );

      case 'CASE_APPROVED':
        return <FaIcons.FaAward size="40" />;

      case 'CASE_RETURNED':
        return <FaIcons.FaThumbsDown size="40" />;

      case 'CASE_ASSIGNED':
        return <FaIcons.FaBriefcase size="40" />;

      case 'CASE_UNASSIGNED':
        return <FaIcons.FaBriefcase size="40" />;

      case 'CERTIFICATION_SUBMITTED':
        return <FaIcons.FaUser size="40" />;

      case 'STAFF_MESSAGE':
        return (
          <span className="fa-layers fa-fw fa-3x">
            <FontAwesomeIcon icon={icon({ name: 'message', style: 'regular' })} />
            <FontAwesomeIcon
              icon={icon({ name: 'triangle-exclamation', style: 'solid' })}
              transform={'shrink-5 up-7 right-7'}
              // mask={icon({ name: 'triangle', style: 'solid' })}
            />
          </span>
        );

      case 'MESSAGE_RECEIVED':
        return (
          <span className="fa-layers fa-fw fa-3x">
            {/* <FaIcons.FaEnvelope size={20} /> */}
            <FontAwesomeIcon icon={icon({ name: 'envelope', style: 'solid' })} />
          </span>
        );

      case 'CASE_FILE_DOWNLOADED':
        return (
          <span className="fa-layers fa-fw fa-3x">
            <FontAwesomeIcon icon={icon({ name: 'arrow-down-to-bracket', style: 'light', family: 'sharp' })} />
          </span>
        );

      case 'AWARD_THRESHOLD_REACHED':
        return <FontAwesomeIcon icon={icon({ name: 'trophy', style: 'solid' })} fontSize={40} />;

      case 'LEO_USER_ONBOARDED':
        // return <FontAwesomeIcon icon={icon({ name: 'user-police', style: 'solid' })} fontSize={40} />;
        return (
          <span className="fa-layers fa-fw fa-3x">
            <FontAwesomeIcon icon={icon({ name: 'user-police', style: 'solid' })} fontSize={40} transform={'left-2'} />
            <FontAwesomeIcon
              icon={icon({ name: 'plus', style: 'solid', family: 'sharp' })}
              transform={'shrink-10 right-6'}
              // mask={icon({ name: 'triangle', style: 'solid' })}
            />
          </span>
        );

      case 'USER_ONBOARDED':
        // return <FontAwesomeIcon icon={icon({ name: 'user-police', style: 'solid' })} fontSize={40} />;
        return (
          <span className="fa-layers fa-fw fa-3x">
            <FontAwesomeIcon icon={icon({ name: 'user', style: 'solid' })} fontSize={40} transform={'left-2'} />
            <FontAwesomeIcon
              icon={icon({ name: 'plus', style: 'solid', family: 'sharp' })}
              transform={'shrink-10 right-6'}
              // mask={icon({ name: 'triangle', style: 'solid' })}
            />
          </span>
        );

      case 'CASE_NULLIFIED':
      case 'CASE_DELETED':
        return (
          <span className="fa-layers fa-fw fa-3x">
            {/* <FaIcons.FaEnvelope size={20} /> */}
            <FontAwesomeIcon icon={icon({ name: 'trash-can', style: 'regular' })} />
          </span>
        );

      default:
        return <FiIcons.FiMessageSquare size="40" />;
    }
  };

  const fetchNotifications = useCallback(
    async ({ limit = 40, types, filters, sort } = {}, nextTok) => {
      if (!user || !user.id) {
        console.warn('No user found, cannot fetch notifications');
        return;
      }
      setLoading(true);
      // console.log(
      //   'Fetching notifications for user:',
      //   user.id,
      //   'Limit:',
      //   limit,
      //   'NextToken:',
      //   nextToken,
      //   'nextTok:',
      //   nextTok,
      //   'LastEvaluated:',
      //   lastEvaluated,
      //   'types:',
      //   types,
      //   'filters:',
      //   filters,
      //   'sort:',
      //   sort

      // );
      // let nextTokenLocal = nextToken;
      let nextTokenLocal = typeof nextTok !== 'undefined' ? nextTok : nextTokenRef.current;
      // if (typeof nextTok !== 'undefined') {
        // console.log('Using provided nextToken:', nextTok);
        // nextToken = nextTok;
        // nextTokenLocal = nextTok;
      // }
      let sortField = 'CREATED_AT';
      switch (sort?.fieldName) {
        case 'createdAt':
          sortField = 'CREATED_AT';
          break;
        case 'readAt':
          sortField = 'READ_AT';
          break;
        case 'type':
          sortField = 'TYPE';
          break;
        case 'title':
          sortField = 'TITLE';
          break;
        default:
          sortField = 'CREATED_AT';
      }
      try {
        const params = {
          // userId: user?.id,
          limit,
          types,
          nextToken: nextTokenLocal,
          nextTokenObj: lastEvaluated,
          searchTerm: filters?.searchTerm || '',
          sortOrder: sort?.direction || 'DESC',
          // sortField: sort?.fieldName || 'CREATED_AT',
          sortField,
        };
        const result = await API.graphql(graphqlOperation(listNotifications, params));
        // console.log('Fetched notifications:', {params, result});
        const { items, nextToken: newNextToken, lastEvaluated: newLastEvaluated } = result.data.listNotifications;
        setNotifications(prev => {
          // const newNotifications = [...new Set([...prev, ...items])];
          const newNotifications = [...prev, ...items].filter(
            (item, index, self) => index === self.findIndex(t => t.id === item.id)
          );
          // console.log('New notifications:', JSON.stringify(newNotifications, null, 2));
          return newNotifications;
        });
        // console.log('Setting new nextToken:', newNextToken);
        setNextToken(newNextToken);
        setLastEvaluated(newLastEvaluated);
        // setNotificationCount(items.length);
      } catch (error) {
        console.error('Error fetching notifications:', error);
      } finally {
        setLoading(false);
      }
    },
    [user, nextToken, lastEvaluated]
  );

  /**
   * Fetch newest notifications. Just like fetchNotifications but always fetches the latest notifications
   * regardless of the nextToken or lastEvaluated state.
   * @param {Object} params - Parameters for fetching notifications
   * @param {number} params.limit - The maximum number of notifications to fetch.
   * @param {Array} params.types - Array of notification types to filter by.
   * @param {Object} params.filters - Additional filters for fetching notifications.
   * @param {Object} params.sort - Sorting options for the notifications.
   * @returns {Promise<void>}
   */
  const fetchNewestNotifications = useCallback(
    async ({ limit = 40, types, filters, sort } = {}) => {
      if (!user || !user.id) {
        console.warn('No user found, cannot fetch notifications');
        return;
      }
      setLoading(true);
      // console.log(
      //   'Fetching newest notifications for user:',
      //   user.id,
      //   'Limit:',
      //   limit,
      //   'types:',
      //   types,
      //   'filters:',
      //   filters,
      //   'sort:',
      //   sort
      // );
      try {
        const result = await API.graphql(
          graphqlOperation(listNotifications, {
            userId: user?.id,
            limit,
            types,
            nextToken: null,
            nextTokenObj: null,
            searchTerm: filters?.searchTerm || '',
            sortOrder: sort?.direction || 'DESC',
            sortField: sort?.fieldName || 'CREATED_AT',
          })
        );
        // console.log('Fetched newest notifications:', result);
        const { items } = result.data.listNotifications;
        // setNotifications(items);
        setNotifications(prev => {
          // const newNotifications = [...new Set([...prev, ...items])];
          const newNotifications = [...prev, ...items].filter(
            (item, index, self) => index === self.findIndex(t => t.id === item.id)
          );
          // console.log('New notifications:', JSON.stringify(newNotifications, null, 2));
          // Order the notifications by createdAt in descending order
          newNotifications.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
          return newNotifications;
        });
        setNextToken(result.data.listNotifications.nextToken);
        setLastEvaluated(result.data.listNotifications.lastEvaluated);
        // setNotificationCount(items.length);
      } catch (error) {
        console.error('Error fetching newest notifications:', error);
      } finally {
        setLoading(false);
      }
    },
    [user]
  );

  const fetchUnreadNotifications = useCallback(async () => {
    if (!user || !user.id) {
      console.warn('No user found, cannot fetch notifications');
      return;
    }
    setLoading(true);
    try {
      const result = await API.graphql(
        graphqlOperation(listUnreadNotifications, {
          userId: user?.id,
          limit: 100,
        })
      );
      const { items } = result.data.listUnreadNotifications;
      setUnreadNotifications(items);
    } catch (error) {
      console.error('Error fetching unread notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const resetNextToken = useCallback(() => {
    setNextToken(null);
    setLastEvaluated(null);
  }, []);

  return (
    <NotificationContext.Provider
      value={{
        loading,
        updating,
        // addNotification,
        notificationCount,
        markAsRead,
        markAsUnread,
        fetchNotifications,
        fetchNewestNotifications,
        notifications,
        unreadNotifications,
        fetchUnreadNotifications,
        getIcon,
        hasMore: !!nextToken || !!lastEvaluated,
        queryNotificationCount,
        resetNotifications,
        setNextToken,
        resetNextToken,
      }}>
      {children}
    </NotificationContext.Provider>
  );
};

NotificationProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

const useNotifications = () => useContext(NotificationContext);

export default NotificationContext;

export { NotificationProvider, useNotifications };
